/* Upgrade modal — opened automatically by the 402 fetch interceptor.
 *
 * The detail object that arrives is the structured 402 body from
 * lopettia-auth's `require_feature`:
 *
 *   {
 *     code: 'FEATURE_GATED',
 *     feature: 'byok' | 'history' | ...,
 *     product: 'aiboard' | 'la99',
 *     current_tier: 'free' | 'pro' | 'team',
 *     current_status: 'none' | 'trial' | ...,
 *     upgrade_url: '...'
 *   }
 *
 * We render a friendly modal with a "Verify email" path if current_status
 * is 'none' (the user hasn't started their trial yet) or "Choose plan"
 * otherwise.
 */

import { setUpgradeHandler } from '../auth';

const MODAL_ID = 'upgrade-modal';

const FEATURE_LABEL: Record<string, string> = {
  byok: 'Bring your own keys',
  history: 'Save your work',
  exports: 'Export to DOCX / PDF / PPTX',
  cloud_synth: 'Cloud-side synthesis',
  optimal_prompt: 'Optimal Prompt generator',
  team_workspace: 'Team workspaces',
  white_label: 'White-label branding',
  priority_support: 'Priority support',
  wizard_unlimited: 'Unlimited Maxwell wizard runs',
  cycle_unlimited: 'Unlimited 8-stage cycle runs',
  intent_briefs: 'Intent brief exports',
  cc_script_export: 'Claude Code script export',
};

interface UpgradeDetail {
  code: string;
  feature: string;
  product: string;
  current_tier: string;
  current_status: string;
  upgrade_url: string;
}

function ensureModalRoot(): HTMLElement {
  let root = document.getElementById(MODAL_ID);
  if (root) return root;
  root = document.createElement('div');
  root.id = MODAL_ID;
  root.className = 'upgrade-modal-root';
  root.setAttribute('aria-hidden', 'true');
  document.body.appendChild(root);
  return root;
}

function close(): void {
  const root = document.getElementById(MODAL_ID);
  if (!root) return;
  root.classList.remove('open');
  root.setAttribute('aria-hidden', 'true');
  setTimeout(() => { root.innerHTML = ''; }, 220);  // wait for fade-out
}

function render(detail: UpgradeDetail): void {
  const root = ensureModalRoot();
  const featureLabel = FEATURE_LABEL[detail.feature] || detail.feature;

  const isUnverified = detail.current_status === 'none';
  const headline = isUnverified
    ? 'Verify your email to unlock the trial'
    : detail.current_tier === 'free'
      ? 'Upgrade to use ' + featureLabel
      : 'Your plan does not include ' + featureLabel;

  const body = isUnverified
    ? `Your 14-day Pro trial starts the moment you confirm your email.
       Check your inbox — we sent you a link when you signed up.`
    : `<strong>${featureLabel}</strong> is part of our paid plans.
       Try Pro free for 14 days — cancel anytime, your data stays yours.`;

  const primaryHref = isUnverified ? '/account.html' : detail.upgrade_url || '/pricing.html';
  const primaryLabel = isUnverified ? 'Go to my account' : 'Choose a plan';

  root.innerHTML = `
    <div class="upgrade-backdrop" data-action="close"></div>
    <div class="upgrade-card" role="dialog" aria-modal="true" aria-labelledby="upgrade-headline">
      <button class="upgrade-close" data-action="close" aria-label="Close">×</button>
      <div class="upgrade-eyebrow">${isUnverified ? 'EMAIL VERIFICATION' : 'FEATURE LOCKED'}</div>
      <h2 class="upgrade-headline" id="upgrade-headline">${headline}</h2>
      <p class="upgrade-body">${body}</p>
      <div class="upgrade-actions">
        <a class="upgrade-btn upgrade-btn-primary" href="${primaryHref}">${primaryLabel}</a>
        <button class="upgrade-btn upgrade-btn-secondary" data-action="close">Maybe later</button>
      </div>
      <div class="upgrade-meta">
        Current plan: <code>${detail.current_tier}</code> · status <code>${detail.current_status}</code>
      </div>
    </div>
  `;
  root.classList.add('open');
  root.setAttribute('aria-hidden', 'false');

  root.querySelectorAll('[data-action="close"]').forEach((el) => {
    el.addEventListener('click', close);
  });
  document.addEventListener('keydown', onEsc);
}

function onEsc(e: KeyboardEvent): void {
  if (e.key === 'Escape') {
    close();
    document.removeEventListener('keydown', onEsc);
  }
}

export function attachUpgradeModal(): void {
  ensureModalRoot();
  // Wire the auth interceptor to this handler
  setUpgradeHandler(render as (d: any) => void);
}
